import { useState, useEffect } from 'react'
import { NavLink } from 'react-router-dom'
import { useTema } from '../context/TemaContext'
import { useAuthStore } from '../store/useAuthStore'
import { useIdioma } from '../context/IdiomaContext'
import { useWorkspaceStore } from '../store/useWorkspaceStore'
import { useGamificationStore } from '../store/useGamificationStore'
import UpgradeModal from './UpgradeModal'
import { canCreateResource } from '../utils/freemiumLimits'
const Sidebar = () => {
  const { oscuro } = useTema()
  const { t } = useIdioma()
  const { user: usuario } = useAuthStore()
  const [abierto, setAbierto] = useState(false)
  const [esMobil, setEsMobil] = useState(window.innerWidth < 768)
  const [showUpgradeModal, setShowUpgradeModal] = useState(false)
  
  const { workspaces, activeWorkspaceId, setActiveWorkspace, addWorkspace } = useWorkspaceStore()
  const { xp, level } = useGamificationStore()

  useEffect(() => {
    const handleResize = () => setEsMobil(window.innerWidth < 768)
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  const links = [
    { to: '/',           label: t('sidebar.inicio'),     emoji: '🏠' },
    { to: '/tareas',     label: t('sidebar.tareas'),     emoji: '✅' },
    { to: '/calendario', label: t('sidebar.calendario'), emoji: '📅' },
    { to: '/planificador', label: t('sidebar.planificador'), emoji: '🕐' },
    { to: '/habitos',    label: t('sidebar.habitos'),    emoji: '🔥' },
    { to: '/notas',      label: t('sidebar.notas'),      emoji: '📝' },
    { to: '/finanzas',   label: t('sidebar.finanzas'),   emoji: '💰' },
    { to: '/gimnasio',   label: t('sidebar.gimnasio'),   emoji: '💪' },
    { to: '/calorias',   label: t('sidebar.calorias'),   emoji: '🍎' },
    { to: '/logros',     label: t('sidebar.logros'),     emoji: '🏆' },
    { to: '/analytics',  label: t('sidebar.analytics'),  emoji: '📊' },
    { to: '/configuraciones', label: t('sidebar.configuraciones'), emoji: '⚙️' },
  ]

  if (usuario?.role === 'ADMIN') {
    links.push({ to: '/admin', label: 'Admin Panel', emoji: '👑' })
  }
 
  const sidebarStyle = {
    width: '14rem',
    minHeight: '100vh',
    background: oscuro ? 'rgba(17, 24, 39, 0.7)' : 'rgba(255, 255, 255, 0.7)',
    backdropFilter: 'blur(16px)',
    WebkitBackdropFilter: 'blur(16px)',
    borderRight: oscuro ? '1px solid rgba(255,255,255,0.05)' : '1px solid rgba(0,0,0,0.05)',
    display: 'flex',
    flexDirection: 'column',
    padding: '1rem',
    ...(esMobil ? {
      position: 'fixed',
      top: 0,
      left: 0,
      zIndex: 40,
      transform: abierto ? 'translateX(0)' : 'translateX(-100%)',
      transition: 'transform 0.3s ease',
    } : {
      position: 'relative',
      transform: 'none',
    })
  }

  return (
    <>
      {esMobil && (
        <button
          onClick={() => setAbierto(!abierto)}
          style={{
            position: 'fixed',
            top: '1rem',
            left: '1rem',
            zIndex: 50,
            width: '2.5rem',
            height: '2.5rem',
            background: oscuro ? 'rgba(17, 24, 39, 0.7)' : 'rgba(255, 255, 255, 0.7)',
            backdropFilter: 'blur(16px)',
            border: oscuro ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.1)',
            borderRadius: '0.75rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '1.1rem',
            cursor: 'pointer',
            color: oscuro ? 'white' : 'black',
            boxShadow: '0 1px 4px rgba(0,0,0,0.1)',
          }}
        >
          {abierto ? '✕' : '☰'}
        </button>
      )}

      {esMobil && abierto && (
        <div
          onClick={() => setAbierto(false)}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.4)',
            backdropFilter: 'blur(4px)',
            zIndex: 30,
          }}
        />
      )}

      <aside style={sidebarStyle}>

        <NavLink to="/" style={{
          fontSize: '1.25rem',
          fontWeight: 700,
          color: '#4f46e5',
          marginBottom: '2rem',
          marginTop: esMobil ? '3rem' : '0.5rem',
          textDecoration: 'none',
          display: 'block',
        }}>
          DayFlow
        </NavLink>

        {/* Workspace Selector */}
        <div className="mb-6 relative group">
          <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1 block">Espacio actual</label>
          <select 
            value={activeWorkspaceId}
            onChange={(e) => {
              if (e.target.value === 'new') {
                if (!canCreateResource('workspaces', workspaces.length, usuario?.plan)) {
                  setShowUpgradeModal(true)
                  return
                }
                const name = prompt("Nombre del nuevo Workspace:")
                if (name) addWorkspace({ name, icon: '💼' })
              } else {
                setActiveWorkspace(e.target.value)
              }
            }}
            className="w-full bg-white/50 dark:bg-gray-800/50 backdrop-blur-sm border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-200 rounded-xl px-3 py-2 text-sm font-semibold outline-none focus:border-indigo-500 transition-colors appearance-none cursor-pointer"
          >
            {workspaces.map(w => (
              <option key={w.id} value={w.id} className="text-black">{w.icon} {w.name}</option>
            ))}
            <option value="new" className="text-indigo-600 font-bold">+ Nuevo Workspace</option>
          </select>
        </div>

        <nav style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', flex: 1 }}>
          {links.map(link => (
            <NavLink
              key={link.to}
              to={link.to}
              end
              onClick={() => setAbierto(false)}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors
                ${isActive
                  ? 'bg-indigo-600/10 dark:bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 font-bold'
                  : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100/50 dark:hover:bg-gray-800/50'
                }`
              }
            >
              <span>{link.emoji}</span>
              <span>{link.label}</span>
            </NavLink>
          ))}
        </nav>

        <div style={{
          marginTop: '1rem',
          paddingTop: '1rem',
          borderTop: oscuro ? '1px solid rgba(255,255,255,0.05)' : '1px solid rgba(0,0,0,0.05)'
        }}>
          {/* Usuario logueado */}
          {usuario && (
            <div style={{
              padding: '0.5rem 0.75rem',
              marginBottom: '0.5rem',
              background: oscuro ? 'rgba(31, 41, 55, 0.5)' : 'rgba(249, 250, 251, 0.5)',
              border: oscuro ? '1px solid rgba(255,255,255,0.05)' : '1px solid rgba(0,0,0,0.05)',
              borderRadius: '1rem',
            }}>
              <div className="flex items-center justify-between">
                <p style={{ fontSize: '0.75rem', fontWeight: 600, color: oscuro ? '#f9fafb' : '#374151' }}>
                  {usuario.nombre}
                </p>
                <div className="bg-indigo-100 dark:bg-indigo-900/50 text-indigo-700 dark:text-indigo-300 text-[10px] font-bold px-2 py-0.5 rounded-full flex items-center gap-1">
                  <span>⭐</span> Lvl {level}
                </div>
              </div>
              <p style={{ fontSize: '0.7rem', color: '#6b7280' }}>{usuario.email}</p>
              
              {/* XP Bar */}
              <div className="mt-2">
                <div className="flex justify-between text-[9px] font-semibold text-gray-400 mb-0.5 uppercase tracking-wider">
                  <span>XP</span>
                  <span>{xp} / {Math.pow(level, 2) * 100}</span>
                </div>
                <div className="w-full bg-gray-200 dark:bg-gray-700 h-1.5 rounded-full overflow-hidden">
                  <div 
                    className="bg-gradient-to-r from-amber-400 to-orange-500 h-full transition-all duration-500" 
                    style={{ width: `${(xp / (Math.pow(level, 2) * 100)) * 100}%` }}
                  />
                </div>
              </div>
              
              <div className="flex items-center justify-between mt-1 pt-1 border-t border-gray-200 dark:border-gray-700">
                <p style={{ fontSize: '0.7rem', color: '#6366f1', fontWeight: 600 }}>
                  {usuario.plan === 'free' ? t('sidebar.planGratuito') : t('sidebar.planPremium')}
                </p>
                {usuario.plan === 'free' && (
                  <button 
                    onClick={() => setShowUpgradeModal(true)}
                    className="text-[9px] uppercase font-bold bg-gradient-to-r from-amber-400 to-orange-500 text-white px-2 py-0.5 rounded-md hover:scale-105 transition-transform"
                  >
                    Upgrade
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Firma */}
          <div style={{ padding: '0.5rem 0.75rem' }}>
            <p style={{ fontSize: '0.75rem', color: oscuro ? '#374151' : '#d1d5db' }}>© DayFlow</p>
            <p style={{ fontSize: '0.75rem', fontWeight: 600, color: '#818cf8' }}>Por MeLlamoAgus</p>
          </div>
        </div>
      </aside>
      
      <UpgradeModal 
        isOpen={showUpgradeModal} 
        onClose={() => setShowUpgradeModal(false)} 
        featureName="Funcionalidad Premium" 
      />
    </>
  )
}

export default Sidebar
